import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { SectionLabel } from '@/components/generals/section-label'
import { JOBS } from '@/constants/jobs'
import { PHOTO } from '@/constants/media'
import { LINKS } from '@/constants/site'

const LedigeStillinger: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='Ledige stillinger'
                description="Jobb i Svenn. Vi lager programvaren over 3000 håndverksbedrifter bruker, og ser etter flere som vil være med. Se ledige stillinger, eller send en åpen søknad."
            />

            <section id='jobb-i-svenn' className='tone-deep grain relative overflow-hidden' data-header='dark'>
                <div className='grid-lines' />

                <div className='wrap relative grid grid-cols-12 items-end gap-x-6 gap-y-14 pt-[calc(var(--header-h)+5rem)] pb-20 md:pb-28'>
                    <div className='col-span-12 lg:col-span-7'>
                        <p className='t-label load-fade text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>Ledige stillinger · Oslo, Bergen eller remote</p>

                        <h1 className='t-mega mt-8'>
                            <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                                <SplitWords text='Bli en av' />
                            </span>
                            <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.3s' } as CSSProperties}>
                                <SplitWords text='gjengen.' start={3} />
                            </span>
                        </h1>

                        <p className='t-lead load-fade mt-10 max-w-xl text-tone-muted pretty' style={{ '--load-delay': '0.7s' } as CSSProperties}>
                            Vi har en sterk teamkultur, der vi stiller høye krav til oss selv og hjelper hverandre. Og vi er en veldig fin gjeng.
                        </p>
                    </div>

                    <div className='col-span-12 lg:col-span-4 lg:col-start-9'>
                        <div className='load-fade aspect-[4/5] overflow-hidden bg-night-raised' style={{ '--load-delay': '0.5s' } as CSSProperties}>
                            <img src={PHOTO.svennTeam} alt='To medarbeidere fra Svenn ler sammen i et møte' fetchPriority='high' decoding='async' className='size-full object-cover' />
                        </div>
                    </div>
                </div>
            </section>

            <section id='stillinger' className='tone-paper relative py-24 md:py-36' data-header='light'>
                <div className='wrap'>
                    <div className='flex flex-wrap items-end justify-between gap-6'>
                        <div>
                            <SectionLabel index='01'>Åpne stillinger</SectionLabel>
                            <h2 className='t-h1 mt-6' data-reveal='words'>
                                <SplitWords text={`${JOBS.length} roller. Én åpen dør.`} />
                            </h2>
                        </div>
                        <p className='t-label tabular text-tone-muted'>{JOBS.length + 1} muligheter</p>
                    </div>

                    <ul className='mt-16 border-t border-tone-fg md:mt-24'>
                        {JOBS.map((job, i) => (
                            <li key={job.slug} data-reveal='rise' style={{ '--d': i } as CSSProperties}>
                                <Link href={`/ledige-stillinger/${job.slug}`} className='job-row group grid grid-cols-12 items-baseline gap-x-6 gap-y-3 border-b border-tone-line py-8 md:py-12' data-cursor='Les'>
                                    <span className='t-label tabular col-span-12 text-tone-muted md:col-span-1'>{String(i + 1).padStart(2, '0')}</span>
                                    <span className='job-title col-span-11 md:col-span-6'>{job.title}</span>
                                    <span className='col-span-12 text-tone-muted pretty md:col-span-4'>
                                        {job.teaser}
                                        <span className='t-label mt-3 block'>{job.place} · Oppstart {job.start.toLowerCase()}</span>
                                    </span>
                                    <ArrowRight className='job-arrow col-span-1 size-6 justify-self-end' aria-hidden='true' />
                                </Link>
                            </li>
                        ))}

                        <li data-reveal='rise' style={{ '--d': JOBS.length } as CSSProperties}>
                            <a href={`mailto:${LINKS.careersEmail}?subject=${encodeURIComponent('Åpen søknad')}`} className='job-row group grid grid-cols-12 items-baseline gap-x-6 gap-y-3 border-b border-tone-line py-8 md:py-12'>
                                <span className='t-label tabular col-span-12 text-tone-muted md:col-span-1'>{String(JOBS.length + 1).padStart(2, '0')}</span>
                                <span className='job-title t-serif col-span-11 md:col-span-6'>Åpen søknad</span>
                                <span className='col-span-12 text-tone-muted pretty md:col-span-4'>
                                    Vil du jobbe i Svenn, men ser ikke en rolle som passer? Send oss en e-post, så tar vi gjerne en prat.
                                    <span className='t-label mt-3 block'>{LINKS.careersEmail}</span>
                                </span>
                                <ArrowUpRight className='job-arrow col-span-1 size-6 justify-self-end' aria-hidden='true' />
                            </a>
                        </li>
                    </ul>
                </div>
            </section>
        </Layout>
    )
}

export default LedigeStillinger
